<panel width="450">
  <h1>{{ 'company.files' | translate }}</h1>
  @if (!folders) {
    <skeletons [defaultSkeletons]="5" />
  }
  @if (folders) {
    <section>
      <button circle (click)="toggleCollapseAllFolders()" [ignoreValidation]="true" [loader]="false"><icon type="folder-tree" /></button>
    </section>
    <ng-container *ngTemplateOutlet="folderList; context:{ folders: folders }" />
    @if (!addNewFolder) {
      <section>
        <button small (click)="showAddNewFolder()" [ignoreValidation]="true"><icon type="plus" />{{ 'company.files.add-new-folder' | translate }}</button>
      </section>
    }
    @if (addNewFolder) {
      <form>
        <section>
          <custom-select [required]="true"
            [selectList]="folderItems"
            [(value)]="selectedFolder"
          [label]="'company.files.parent-folder' | translate"></custom-select>
          <custom-input #folderNameInput
            [required]="true"
            [(value)]="newFolderName"
            [label]="'company.files.new-folder-name' | translate"
            [validation]="validationTypeEnum.Folder"
            [customValidState]="!folderExists(newFolderName)"
          [customValidationMessage]="folderExists(newFolderName) ? ('company.files.folder-exists' | translate) : undefined"></custom-input>
        </section>
        <section>
          <button ghost (click)="addNewFolder = false" [loader]="false" [ignoreValidation]="true">{{ 'general.cancel' | translate }}</button>
          <button confirm (click)="createFolder()" [loader]="false">{{ 'company.files.create-folder' | translate }}</button>
        </section>
      </form>
    }
  }
</panel>

<ng-template #folderList let-folders="folders" let-parentList="parentList">
  <list-view #listView [emptyLabel]="'company.files.files-list-empty-label'" [parentList]="parentList" scrollable>
    <header defaultSort="ascending" [sortColumn]="false">{{ 'company.files.folder-name' | translate }}</header>
    <header align-right [sortColumn]="false">{{ 'company.files.folder-date' | translate }}</header>
    <header [sortColumn]="false"></header>
    <row *listData="let folder from folders"
      statusIcon="folder-open"
      [showCollapseIcon]="parentList ? true : false"
      [collapsed]="folder.collapsed"
      (collapsedChange)="folder.collapsed = $event"
      [highlighted]="selectedFolder == folder"
      (action)="selectedFolder = folder">

      <column [ngClass]="{'italic': folder.temporary}">{{ folder.name }}</column>
      <column align-right class="out-of-focus">{{ folder.date | customDate: 'shortDate' }}</column>
      <column align-right>
      <icon type="download" (click)="downloadFolder(folder)" onclick="event.stopPropagation();" />
      <icon type="trash-alt" (click)="deleteFolder(folder)" onclick="event.stopPropagation();" />
    </column>

    @if (folder.children.length > 0 && !folder.collapsed) {
      <ng-container *ngTemplateOutlet="folderList; context:{ folders: folder.children, parentList: listView }" />
    }
  </row>
</list-view>
</ng-template>

<panel width="700">
  @if (!selectedFolder) {
    <h1><skeleton /></h1>
  }
  @if (selectedFolder) {
    <editable-content *editableData="let editFolder from selectedFolder" (valueChanged)="renameFolder(selectedFolder, $event.name)" [editEnabled]="selectedFolder.path.length > 0">
      <display-content>
        <h1>{{ selectedFolder.name }} <icon type="link-horizontal" (click)="$event.stopPropagation(); navigation.copyUrl();" /></h1>
      </display-content>
      <edit-content>
        @if (editFolder.value) {
          <section><custom-input [validation]="validationTypeEnum.Folder" [(value)]="editFolder.value.name" /></section>
        }
      </edit-content>
    </editable-content>
  }
  @if (!folderFiles) {
    <skeletons [defaultSkeletons]="5" />
  }
  @if (folderFiles) {
    <list-view [emptyLabel]="'company.files.files-list-empty-label'" [selectable]="false" scrollable (drop)="uploader?.dragUploader?.onDrop($event)" (dragover)="$event.preventDefault()">
      <header [sortColumn]="false"><input type="checkbox" #toggleAll [checked]="allFilesAreSelected()" (click)="toggleAllFolderFiles(toggleAll.checked);" /></header>
      <header defaultSort="ascending">{{ 'company.files.file-name' | translate }}</header>
      <header align-right>{{ 'company.files.uploaded' | translate }}</header>
      <header [sortColumn]="false"></header>
      <row *listData="let file from folderFiles" [statusColor]="getStatusColor(file)" [statusIcon]="getStatusIcon(file)" [clickable]="getStatusIcon(file) == 'file-image'" [highlighted]="previewFile == file" (action)="previewFile = file" #editorPlacement>
        <column>@if (!file.$deleted) {
        <input type="checkbox" (change)="toggleFile(file);" onclick="event.stopPropagation();" [checked]="selectedFiles.indexOf(file) >= 0" />
      }</column>
      <column>{{ filename(file) }}</column>
      <column align-right [sortValue]="file.fileReference.created">{{ file.fileReference.created | customDate: 'shortDate' }}</column>
      <column align-right><icon hover clickable type="pen-to-square" (click)="enableRenameFile(file, editorPlacement)" /></column>
    </row>
  </list-view>
}
<editable-content #fileNameEditor [offsetPosition]="editorOffsetPosition" *editableData="let editName" (valueChanged)="renameFile(editNameOfFile, $event)">
  <edit-content>
    <section><custom-input [suffix]="renameFileExtension" [validation]="validationTypeEnum.Folder" [(value)]="editName.value" /></section>
  </edit-content>
</editable-content>

@if (selectedFiles.length > 0) {
  <section>
    <button #deleteButton reject (click)="deleteSelectedFiles()" [ignoreValidation]="true"><icon type="trash" />{{ 'company.files.delete-selected-files' | translate }}</button>
    <button #downloadButton confirm (click)="downloadSelectedFiles()" [progression]="downloadProgress" [ignoreValidation]="true">
      <icon type="download" />{{ (downloadProgress ? 'company.files.files-downloaded' : 'company.files.download-selected-files') | translate }}
    </button>
  </section>
}
@if (selectedFolder) {
  <section>
    <file-upload [label]="'company.files.upload' | translate"
                 [hideUploadedFiles]="true"
                 [currentFolder]="selectedFolder.path"
                 [finalizeUrl]="'/api/upload/create-company-attachments/' + companyId" />
  </section>
}
</panel>

@if (previewFile) {
  <panel>
    <h1>{{ previewFile.fileReference?.fileName }}</h1>
    <section scrollable>
      @if (!previewFileUrl) {
        <skeleton height="300px" />
      }
      @if (previewFileUrl) {
        <img [src]="previewFileUrl" style="max-width: 100%;" />
      }
    </section>
  </panel>
}
